import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Skeleton } from '@/components/ui/skeleton'
import { SectionCard } from './SectionCard'
import { useJobsByCategory } from '@/hooks/useDashboard'
import { useDashboardPeriod } from './DashboardPeriodContext'
import { cn } from '@/lib/utils'

function CategoryChartIcon() {
  return (
    <svg
      width='16'
      height='16'
      viewBox='0 0 16 16'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      aria-hidden
    >
      <g transform='translate(0.3332 0.3349)'>
        <path
          d='M13.8069 10.26C13.3828 11.263 12.7195 12.1468 11.8749 12.8342C11.0302 13.5216 10.0301 13.9916 8.96189 14.2032C7.89367 14.4148 6.78988 14.3614 5.74703 14.0479C4.70418 13.7343 3.75402 13.17 2.97962 12.4044C2.20521 11.6388 1.63015 10.6952 1.30469 9.65597C0.97924 8.61676 0.91331 7.51366 1.11267 6.44309C1.31202 5.37252 1.7706 4.36709 2.4483 3.51469C3.126 2.66229 4.00219 1.98888 5.00027 1.55333M14.3336 7.66667C14.3336 6.79119 14.1612 5.92428 13.8261 5.11544C13.4911 4.30661 13 3.57168 12.381 2.95262C11.7619 2.33356 11.027 1.8425 10.2182 1.50747C9.40932 1.17244 8.54242 1 7.66694 1V7.66667H14.3336Z'
          stroke='#2550CC'
          strokeWidth='2'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </g>
    </svg>
  )
}

const PALETTE = [
  '#10B981',
  '#2E5CE6',
  '#FA541C',
  '#10B981',
  '#9254DE',
  '#EFAC00',
  '#EA3FB8',
  '#BAE637',
  '#36CFC9',
  '#FF7A45',
  '#FFA940',
]

type JobsByCategoryDonutChartProps = {
  labels: string[]
  values: number[]
}

function JobsByCategoryDonutChart({
  labels,
  values,
}: JobsByCategoryDonutChartProps) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
  const maxValue = Math.max(1, ...values)

  return (
    <div className='scrollbar-none flex max-h-61 flex-col gap-3 overflow-x-hidden overflow-y-auto'>
      {labels.map((label, i) => {
        const isHovered = hoveredIndex === i
        const isDimmed = hoveredIndex !== null && !isHovered
        const color = PALETTE[i % PALETTE.length]

        return (
          <button
            key={label}
            type='button'
            className={cn(
              'flex h-5 w-full shrink-0 items-center gap-3 text-left transition-opacity duration-150',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              isDimmed && 'opacity-40'
            )}
            onMouseEnter={() => setHoveredIndex(i)}
            onMouseLeave={() => setHoveredIndex(null)}
            onFocus={() => setHoveredIndex(i)}
            onBlur={() => setHoveredIndex(null)}
          >
            <span className='flex w-60 max-w-[45%] shrink-0 items-center gap-2'>
              <span
                className={cn(
                  'size-2 shrink-0 rounded-full transition-transform duration-150',
                  isHovered && 'scale-125'
                )}
                style={{ backgroundColor: color }}
              />
              <span className='min-w-0 truncate font-sans text-xs font-normal leading-[1.4] text-[#171a20]'>
                {label}
              </span>
            </span>
            <span className='h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-[#f4f5f8]'>
              <span
                className='block h-full rounded-full transition-[width] duration-150'
                style={{
                  width: `${((values[i] ?? 0) / maxValue) * 100}%`,
                  backgroundColor: color,
                }}
              />
            </span>
            <span className='min-w-8 shrink-0 text-right font-sans text-xs font-medium leading-[1.4] text-[#171a20] tabular-nums'>
              {values[i] ?? 0}
            </span>
          </button>
        )
      })}
    </div>
  )
}

export function JobsByCategoryDonut() {
  const { t } = useTranslation('dashboard')
  const { period } = useDashboardPeriod()
  const { data, isLoading, isError } = useJobsByCategory(period)
  const labels = data?.data?.labels ?? []
  const values = data?.data?.series?.[0]?.data ?? []
  const total = values.reduce((a, b) => a + b, 0)
  const chartKey = `${period}|${labels.join('|')}`

  let body: React.ReactNode
  if (isLoading) {
    body = <Skeleton className='mx-auto h-40 w-40 rounded-full' />
  } else if (isError) {
    body = (
      <p className='py-12 text-center text-sm text-destructive'>
        {t('jobsByCategory.unableToLoad')}
      </p>
    )
  } else if (labels.length === 0 || total === 0) {
    body = (
      <p className='py-12 text-center text-sm text-muted-foreground'>
        {t('jobsByCategory.empty')}
      </p>
    )
  } else {
    body = (
      <JobsByCategoryDonutChart
        key={chartKey}
        labels={labels}
        values={values}
      />
    )
  }

  return (
    <SectionCard
      variant='chart'
      title={t('jobsByCategory.title')}
      className='h-full'
      contentClassName='h-[292px]'
      headerIcon={<CategoryChartIcon />}
      headerIconClassName='bg-[#ebf1fe]'
      headerRight={t('jobsByCategory.totalThisMonth', { count: total })}
    >
      {body}
    </SectionCard>
  )
}

export default JobsByCategoryDonut
